import { Interweave } from "interweave";
import { motion } from "motion/react";
import React, { useRef } from "react";
import { Variants } from "../../animations/variants";
import { isExactNear, isNoMatch } from "../../lib/routing";
import { SearchResultsArticles } from "./articles";
import SearchResultsEmpty from "./empty";
import { SearchResultsQuestions } from "./questions";
import { ConversationsCreateData } from "../../websockets/conversations/use_conversations_message_handler";

export enum SearchResultsSectionOrder {
  First = "first",
  Second = "second",
}

type SearchResultsDataProps = {
  data?: ConversationsCreateData;
};

export default function SearchResultsData({ data }: SearchResultsDataProps) {
  // We want to keep the last valid data around so that it continues to appear
  // while it is transitioning off the screen
  const lastValidData = useRef<ConversationsCreateData | undefined>(undefined);
  if (data) lastValidData.current = data;

  // Destructure the data we're presenting to get the relevant parts
  const { complete, content, routing, questions, articles } = lastValidData.current ?? {};

  // Check if we have any results to display
  if (complete && (isNoMatch(routing) || (!content?.length && !questions?.length && !articles?.length))) {
    return <SearchResultsEmpty routing={routing} />;
  }

  // Determine the order of the sections if we have content
  let questionsOrder: SearchResultsSectionOrder = SearchResultsSectionOrder.First;
  let articlesOrder: SearchResultsSectionOrder = SearchResultsSectionOrder.Second;
  if (isExactNear(routing)) {
    questionsOrder = SearchResultsSectionOrder.Second;
    articlesOrder = SearchResultsSectionOrder.First;
  }

  return (
    <div className="dewey--search--results--data">
      {content?.length && (
        <motion.div
          key="data--content"
          variants={Variants.FadeInUp}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <div className="dewey--search--results--data--content">
            <Interweave content={content} />
          </div>
        </motion.div>
      )}
      {complete && (
        <>
          <SearchResultsQuestions
            routing={routing}
            questions={questions}
            order={questionsOrder}
          />
          <SearchResultsArticles
            routing={routing}
            articles={articles}
            order={articlesOrder}
          />
        </>
      )}
    </div>
  );
}
